/* ═══════════════════════════════════════════════════════════════════════════
   RALJE — LUSTRE
   Volume III · MMXXVI
   ---------------------------------------------------------------------------
   The light layer. Gold is never paint here — only light — so everything in
   this file is about how light behaves on metal: dust caught in a beam, a
   sweep travelling across a polished face, the small flare a stone throws
   when it turns.

   Everything is opt-in through data attributes, so no existing page changes
   behaviour unless it asks to. Everything is disabled under
   prefers-reduced-motion, on coarse pointers where it would only cost
   battery, and when the tab is not being looked at.

     [data-lustre-dust]      a slow field of gold dust behind a section
     [data-lustre-spark]     a flare of diamonds when clicked
     [data-lustre-magnet]    pulls very slightly toward the cursor
     [data-lustre-tilt]      turns a few degrees to follow the cursor
     [data-lustre-beam]      a bench light that follows the cursor
     [data-lustre-cut]       a heading that is uncovered rather than faded
   ═════════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  var REDUCED = window.matchMedia &&
                window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var FINE    = window.matchMedia &&
                window.matchMedia('(pointer: fine)').matches;

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function onFrame(fn) {
    var queued = false;
    return function () {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(function () {
        queued = false;
        fn();
      });
    };
  }

  var GOLD = [
    [232, 206, 155],
    [201, 160,  92],
    [184, 137,  62],
    [255, 246, 228]
  ];

  function pickGold() {
    return GOLD[Math.floor(Math.random() * GOLD.length)];
  }


  /* ──────────────────────────────────────────────────────────────────────
     01 · GOLD DUST
     A section-scoped canvas. Particles drift upward the way filings do in
     the draught over a bench, brighten as they cross the middle, and are
     recycled at the top. One canvas per marked section, paused when the
     section is off screen.
     ────────────────────────────────────────────────────────────────────── */

  function initDust() {
    if (REDUCED) return;

    var hosts = document.querySelectorAll('[data-lustre-dust]');
    if (!hosts.length) return;

    Array.prototype.forEach.call(hosts, function (host) {
      var canvas = document.createElement('canvas');
      canvas.className = 'lustre-dust';
      canvas.setAttribute('aria-hidden', 'true');
      host.insertBefore(canvas, host.firstChild);

      var ctx = canvas.getContext('2d');
      if (!ctx) return;

      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      var w = 0, h = 0;
      var motes = [];
      var running = false;
      var raf = null;

      var density = parseFloat(host.getAttribute('data-lustre-dust')) || 1;

      function build() {
        var rect = host.getBoundingClientRect();
        w = Math.max(rect.width, 1);
        h = Math.max(rect.height, 1);

        canvas.width  = Math.floor(w * dpr);
        canvas.height = Math.floor(h * dpr);
        canvas.style.width  = w + 'px';
        canvas.style.height = h + 'px';
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        // Roughly one mote per 11,000px² — enough to read as dust, never snow.
        var count = Math.round((w * h) / 11000 * density);
        count = Math.max(10, Math.min(count, 130));

        motes = [];
        for (var i = 0; i < count; i++) motes.push(makeMote(true));
      }

      function makeMote(seeded) {
        var rgb = pickGold();
        return {
          x: Math.random() * w,
          y: seeded ? Math.random() * h : h + Math.random() * 40,
          r: Math.random() * 1.15 + 0.35,
          vy: -(Math.random() * 0.17 + 0.045),
          vx: (Math.random() - 0.5) * 0.09,
          a: Math.random() * 0.42 + 0.08,
          tw: Math.random() * Math.PI * 2,
          tws: Math.random() * 0.017 + 0.005,
          rgb: rgb
        };
      }

      function frame() {
        ctx.clearRect(0, 0, w, h);

        for (var i = 0; i < motes.length; i++) {
          var m = motes[i];

          m.y += m.vy;
          m.x += m.vx;
          m.tw += m.tws;

          if (m.y < -8) { motes[i] = makeMote(false); continue; }
          if (m.x < -8) m.x = w + 8;
          if (m.x > w + 8) m.x = -8;

          // Brightest through the middle third — as if crossing the beam.
          var band = 1 - Math.abs((m.y / h) - 0.5) * 1.5;
          if (band < 0) band = 0;

          var alpha = m.a * (0.55 + Math.sin(m.tw) * 0.45) * (0.4 + band * 0.6);
          if (alpha <= 0.004) continue;

          ctx.beginPath();
          ctx.arc(m.x, m.y, m.r, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(' + m.rgb[0] + ',' + m.rgb[1] + ',' + m.rgb[2] + ',' + alpha.toFixed(3) + ')';
          ctx.fill();
        }

        raf = window.requestAnimationFrame(frame);
      }

      function start() {
        if (running) return;
        running = true;
        raf = window.requestAnimationFrame(frame);
      }

      function stop() {
        running = false;
        if (raf) window.cancelAnimationFrame(raf);
        raf = null;
      }

      build();

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting && !document.hidden) start();
            else stop();
          });
        }, { rootMargin: '120px' }).observe(host);
      } else {
        start();
      }

      document.addEventListener('visibilitychange', function () {
        if (document.hidden) stop();
      });

      var rebuild = onFrame(build);
      window.addEventListener('resize', rebuild);

      if ('ResizeObserver' in window) {
        new ResizeObserver(rebuild).observe(host);
      }
    });
  }


  /* ──────────────────────────────────────────────────────────────────────
     02 · THE FLARE
     What a cut stone does when it catches the light — a handful of small
     diamonds thrown outward and gone. Fired on click, so it reads as the
     answer to an action rather than decoration.
     ────────────────────────────────────────────────────────────────────── */

  function flare(x, y, count) {
    if (REDUCED) return;

    var layer = document.getElementById('lustreFlare');
    if (!layer) {
      layer = document.createElement('div');
      layer.id = 'lustreFlare';
      layer.className = 'lustre-flare';
      layer.setAttribute('aria-hidden', 'true');
      document.body.appendChild(layer);
    }

    var n = count || 13;
    for (var i = 0; i < n; i++) {
      var bit = document.createElement('span');
      bit.className = 'lustre-flare__bit';

      var angle = (Math.PI * 2 * i) / n + (Math.random() - 0.5) * 0.7;
      var dist  = 26 + Math.random() * 58;
      var rgb   = pickGold();

      bit.style.left = x + 'px';
      bit.style.top  = y + 'px';
      bit.style.setProperty('--dx', (Math.cos(angle) * dist).toFixed(1) + 'px');
      bit.style.setProperty('--dy', (Math.sin(angle) * dist - 14).toFixed(1) + 'px');
      bit.style.setProperty('--sz', (Math.random() * 3.6 + 1.8).toFixed(1) + 'px');
      bit.style.setProperty('--dur', (620 + Math.random() * 460).toFixed(0) + 'ms');
      bit.style.setProperty('--tint', 'rgb(' + rgb[0] + ',' + rgb[1] + ',' + rgb[2] + ')');

      layer.appendChild(bit);

      (function (el) {
        window.setTimeout(function () {
          if (el && el.parentNode) el.parentNode.removeChild(el);
        }, 1150);
      })(bit);
    }
  }

  function initSpark() {
    if (REDUCED) return;

    document.addEventListener('click', function (e) {
      var target = e.target && e.target.closest
        ? e.target.closest('[data-lustre-spark]')
        : null;
      if (!target) return;
      flare(e.clientX, e.clientY, 14);
    }, { passive: true });
  }


  /* ──────────────────────────────────────────────────────────────────────
     03 · MAGNETISM
     A control that leans toward the hand. Two or three pixels — enough to
     feel, not enough to notice.
     ────────────────────────────────────────────────────────────────────── */

  function initMagnet() {
    if (REDUCED || !FINE) return;

    var nodes = document.querySelectorAll('[data-lustre-magnet]');
    if (!nodes.length) return;

    Array.prototype.forEach.call(nodes, function (el) {
      var strength = parseFloat(el.getAttribute('data-lustre-magnet')) || 0.22;
      var pending = false;
      var mx = 0, my = 0;

      function apply() {
        pending = false;
        el.style.transform = 'translate3d(' + mx.toFixed(2) + 'px,' + my.toFixed(2) + 'px,0)';
      }

      el.addEventListener('mousemove', function (e) {
        var r = el.getBoundingClientRect();
        mx = (e.clientX - (r.left + r.width / 2)) * strength;
        my = (e.clientY - (r.top + r.height / 2)) * strength;
        if (!pending) { pending = true; window.requestAnimationFrame(apply); }
      });

      el.addEventListener('mouseleave', function () {
        mx = 0; my = 0;
        el.style.transform = '';
      });
    });
  }


  /* ──────────────────────────────────────────────────────────────────────
     04 · TILT
     A card held up and turned. The glare travels the opposite way, which is
     what makes the surface read as something rather than a rectangle.
     ────────────────────────────────────────────────────────────────────── */

  function initTilt() {
    if (REDUCED || !FINE) return;

    var nodes = document.querySelectorAll('[data-lustre-tilt]');
    if (!nodes.length) return;

    Array.prototype.forEach.call(nodes, function (el) {
      var max = parseFloat(el.getAttribute('data-lustre-tilt')) || 7;
      var pending = false;
      var rx = 0, ry = 0, gx = 50, gy = 50;

      el.classList.add('lustre-tilt');

      function apply() {
        pending = false;
        el.style.transform =
          'perspective(900px) rotateX(' + rx.toFixed(2) + 'deg) rotateY(' + ry.toFixed(2) + 'deg)';
        el.style.setProperty('--glare-x', gx.toFixed(1) + '%');
        el.style.setProperty('--glare-y', gy.toFixed(1) + '%');
      }

      el.addEventListener('mousemove', function (e) {
        var r = el.getBoundingClientRect();
        var px = (e.clientX - r.left) / r.width;
        var py = (e.clientY - r.top) / r.height;

        ry = (px - 0.5) * max * 2;
        rx = (0.5 - py) * max * 2;
        gx = px * 100;
        gy = py * 100;

        if (!pending) { pending = true; window.requestAnimationFrame(apply); }
      });

      el.addEventListener('mouseleave', function () {
        el.style.transform = '';
        el.style.setProperty('--glare-x', '50%');
        el.style.setProperty('--glare-y', '-30%');
      });
    });
  }


  /* ──────────────────────────────────────────────────────────────────────
     05 · THE BENCH LIGHT
     A soft warm pool that follows the cursor across a marked section, the
     way a jeweller's lamp does when it is swung over the work.
     ────────────────────────────────────────────────────────────────────── */

  function initBeam() {
    if (REDUCED || !FINE) return;

    var hosts = document.querySelectorAll('[data-lustre-beam]');
    if (!hosts.length) return;

    Array.prototype.forEach.call(hosts, function (host) {
      host.classList.add('lustre-beam');

      var pending = false;
      var x = 50, y = 50;

      function apply() {
        pending = false;
        host.style.setProperty('--beam-x', x.toFixed(1) + '%');
        host.style.setProperty('--beam-y', y.toFixed(1) + '%');
      }

      host.addEventListener('mousemove', function (e) {
        var r = host.getBoundingClientRect();
        x = ((e.clientX - r.left) / r.width) * 100;
        y = ((e.clientY - r.top) / r.height) * 100;
        if (!pending) { pending = true; window.requestAnimationFrame(apply); }
      });

      host.addEventListener('mouseenter', function () { host.classList.add('is-lit'); });
      host.addEventListener('mouseleave', function () { host.classList.remove('is-lit'); });
    });
  }


  /* ──────────────────────────────────────────────────────────────────────
     06 · THE CUT
     Headings are not faded in. They are uncovered — a mask drawn back from
     the left, the way a cloth comes off a finished piece.
     ────────────────────────────────────────────────────────────────────── */

  function initCut() {
    var nodes = document.querySelectorAll('[data-lustre-cut]');
    if (!nodes.length) return;

    if (REDUCED || !('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(nodes, function (el) { el.classList.add('is-cut'); });
      return;
    }

    Array.prototype.forEach.call(nodes, function (el) {
      el.classList.add('lustre-cut');
    });

    // A clipped element reports no intersecting area of its own, so watch the
    // container it sits in and uncover the heading from there. Without this
    // the mask never lifts and the heading stays invisible.
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;

        var targets = entry.target.__lustreCut || [];
        targets.forEach(function (el) { el.classList.add('is-cut'); });
        io.unobserve(entry.target);
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -6% 0px' });

    Array.prototype.forEach.call(nodes, function (el) {
      var watch = el.parentElement || el;
      if (!watch.__lustreCut) {
        watch.__lustreCut = [];
        io.observe(watch);
      }
      watch.__lustreCut.push(el);
    });

    // A heading that never uncovers is worse than one that never animates.
    // If anything at all has gone wrong, show them.
    window.setTimeout(function () {
      Array.prototype.forEach.call(nodes, function (el) { el.classList.add('is-cut'); });
    }, 2600);
  }


  /* ──────────────────────────────────────────────────────────────────────
     07 · ADOPTION
     The house was built before this layer existed. Rather than edit every
     section, the handful of elements that should carry light are marked
     here, once, at boot. Anything already marked in its own markup is left
     exactly as it is.
     ────────────────────────────────────────────────────────────────────── */

  var ADOPT = [
    // The gold foil controls — a flare when struck, and a slight lean.
    { sel: '.hero__cta, .atelier-nav__cta, .ralje-page-nav__cta, .ls-cta',
      attrs: { 'data-lustre-spark': '', 'data-lustre-magnet': '0.14' } },

    // Forms answer with a flare, but should not move under the cursor.
    { sel: '.form-submit',
      attrs: { 'data-lustre-spark': '' } },

    // Dust hangs in the air where the Ledger is spoken about.
    { sel: '[id^="LedgerShowcase-"]',
      attrs: { 'data-lustre-dust': '0.5' } }
  ];

  function adopt() {
    ADOPT.forEach(function (rule) {
      var nodes;
      try { nodes = document.querySelectorAll(rule.sel); }
      catch (err) { return; }

      Array.prototype.forEach.call(nodes, function (el) {
        Object.keys(rule.attrs).forEach(function (name) {
          if (!el.hasAttribute(name)) el.setAttribute(name, rule.attrs[name]);
        });
      });
    });
  }


  /* ──────────────────────────────────────────────────────────────────────
     BOOT
     ────────────────────────────────────────────────────────────────────── */

  ready(function () {
    adopt();
    initDust();
    initSpark();
    initMagnet();
    initTilt();
    initBeam();
    initCut();
  });

  // Let sections fire a flare of their own — the reservation uses it the
  // moment a month is chosen.
  window.RaljeLustre = { flare: flare };
})();
